import * as React from 'react';

import Grid, { GridProps } from '@mui/material/Grid';

import { User } from '../../../graphql/API';
import useSubscriptionAccess from '../../../hooks/useSubscriptionAccess';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { extractSubscriptionPlan } from '../../SubscriptionPlans/common/helper';
import { EmptyCollab } from './EmptyCollab';
import ProfileCard from './ProfileCard';
import ProfileCardSkeleton from './ProfileCardSkeleton';
import { OnChange } from './types';

type ProfileCheckBoxGroupProps = {
  data: User[];
  isLoading: boolean;
  isFetching: boolean;
  onChange: (values: string[]) => void;
  values: string[];
  handleMarketChange?: () => void;
};

const commonGrid = (key: string): GridProps => ({
  item: true,
  key,
  xs: 2.4,
  md: 3,
  lg: 3,
  xl: 2.4,
});

const ProfileCheckBoxGroup: React.FC<ProfileCheckBoxGroupProps> = ({
  data,
  isLoading,
  isFetching,
  onChange,
  values,
  handleMarketChange,
}) => {
  const venueState = useAppSelector((state) => state.venue.value);

  const { subscription } = useAppSelector((state) => state.subscription.value);
  const subscriptionAccess = useSubscriptionAccess();

  const handleOnChanged = React.useCallback<OnChange>(
    (e, checked) => {
      const v = e.target.value;

      const vv = checked ? [...values, v] : values.filter((y) => y !== v);
      onChange(vv);
    },
    [onChange, values],
  );

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  return (
    <>
      <Grid container spacing={3} rowSpacing={10}>
        {isLoading
          ? [...Array(6)].map((_, i) => (
              <Grid {...commonGrid(`grid-skeleton-${i}`)} key={`skeleton-${i}`}>
                <ProfileCardSkeleton />
              </Grid>
            ))
          : data.map((x) => (
              <Grid {...commonGrid(x.id)} key={x.id}>
                <ProfileCard
                  data={x}
                  fullProfile={subscriptionAccess?.includes('COMPLETE_USER_PROFILES')}
                  onChange={handleOnChanged}
                  checked={values.includes(x.id)}
                  profileLinkDisabled={
                    !subscriptionPlan && venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)
                  }
                />
              </Grid>
            ))}
      </Grid>
      {!isFetching && data.length === 0 && <EmptyCollab handleMarketChange={handleMarketChange} />}
    </>
  );
};

export default ProfileCheckBoxGroup;
